import type { ReportsIndex } from '@/types';
import { cn } from '@/lib/utils';
import { Button } from '@astryxdesign/core/Button';
import { IconButton } from '@astryxdesign/core/IconButton';
import { Text } from '@astryxdesign/core/Text';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useLayoutEffect, useRef } from 'react';
import { formatShortDate, weekdayName } from '../lib/format';

type Day = ReportsIndex['days'][number];

interface DayTabsProps {
  days: Day[];
  selectedDate: string;
  onSelect: (date: string) => void;
}

export default function DayTabs({ days, selectedDate, onSelect }: DayTabsProps) {
  const stripRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);
  const isFirstRender = useRef(true);

  // Center the selected tab using getBoundingClientRect so the calculation is
  // independent of offsetParent (which may be a distant ancestor rather than
  // the scroll container). Returns the target scrollLeft that places the
  // selected tab in the middle of the visible strip.
  const centeredScrollLeft = useCallback((strip: HTMLDivElement, selected: HTMLButtonElement) => {
    const stripRect = strip.getBoundingClientRect();
    const selectedRect = selected.getBoundingClientRect();
    const offsetWithinStrip = selectedRect.left - stripRect.left + strip.scrollLeft;
    return offsetWithinStrip - (strip.clientWidth - selectedRect.width) / 2;
  }, []);

  const animateTo = useCallback((strip: HTMLDivElement, target: number, duration: number) => {
    const start = strip.scrollLeft;
    const distance = target - start;
    if (distance === 0) return;

    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (startTime === null) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      // ease-out cubic
      const eased = 1 - (1 - progress) ** 3;
      strip.scrollLeft = start + distance * eased;
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, []);

  // On mount, snap the selected day into place instantly (no flicker on the
  // very first paint). On later date changes, ease into the new position with
  // a short rAF animation so the transition feels intentional.
  useLayoutEffect(() => {
    const strip = stripRef.current;
    const selected = selectedRef.current;
    if (!strip || !selected) return;

    const target = centeredScrollLeft(strip, selected);

    if (isFirstRender.current) {
      isFirstRender.current = false;
      strip.scrollLeft = target;
      return;
    }

    animateTo(strip, target, 200);
  }, [selectedDate, centeredScrollLeft, animateTo]);

  // Keep the selected day centered when the strip is resized (window resize,
  // sidebar collapse, etc.). Snap instantly since this isn't a user action.
  useLayoutEffect(() => {
    const strip = stripRef.current;
    if (!strip || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => {
      const selected = selectedRef.current;
      if (selected) strip.scrollLeft = centeredScrollLeft(strip, selected);
    });
    observer.observe(strip);
    return () => observer.disconnect();
  }, [centeredScrollLeft]);

  const today = days.find((day) => day.isToday)?.date;
  const selectedIndex = days.findIndex((day) => day.date === selectedDate);
  const hasPrev = selectedIndex > 0;
  const hasNext = selectedIndex >= 0 && selectedIndex < days.length - 1;

  return (
    <div className="iorio-reports-daynav border-border border-b">
      <header className="flex items-center justify-between gap-3 px-5 pt-5">
        <Text type="supporting" weight="bold" className="tracking-wider uppercase">
          Calendario
        </Text>
        <Button
          label="Hoy"
          variant="secondary"
          size="sm"
          isDisabled={!today || selectedDate === today}
          onClick={() => today && onSelect(today)}
        />
      </header>

      <div className="flex items-center gap-2 px-3 pt-3 pb-4">
        <IconButton
          label="Día anterior"
          tooltip="Día anterior"
          icon={<ChevronLeft size={16} />}
          variant="ghost"
          size="sm"
          isDisabled={!hasPrev}
          onClick={() => hasPrev && onSelect(days[selectedIndex - 1].date)}
        />

        <div
          ref={stripRef}
          role="navigation"
          className="iorio-reports-daystrip"
          aria-label="Días del parte diario"
        >
          {days.map((day) => {
            const isSelected = day.date === selectedDate;
            const isLocked = !day.editable && !day.planned && !day.isToday;

            return (
              <button
                key={day.date}
                ref={isSelected ? selectedRef : undefined}
                type="button"
                aria-current={isSelected ? 'date' : undefined}
                onClick={() => onSelect(day.date)}
                className={cn(
                  'min-w-33 flex-none cursor-pointer rounded-2xl border-2 px-4 py-3 text-left transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--iorio-accent)]',
                  isSelected
                    ? 'border-[var(--iorio-accent)] bg-[var(--iorio-accent-soft)]'
                    : 'border-border bg-card',
                  !isSelected && day.planned && day.versionCount === 0 && 'border-dashed',
                  !isSelected && isLocked && 'opacity-60'
                )}
              >
                <div className="flex items-center justify-center gap-2">
                  <span
                    className={cn(
                      'text-[15px] font-extrabold tracking-tight',
                      day.planned ? 'text-secondary' : 'text-primary'
                    )}
                  >
                    {formatShortDate(day.date)}
                  </span>
                </div>
                <div className="text-secondary mt-1 text-center text-[11px] font-semibold">
                  {day.isToday ? 'Hoy' : weekdayName(day.date)}
                </div>
                <div className="mt-2 flex h-5 items-center justify-center">
                  {day.versionCount > 0 ? (
                    <span title="Guardado" className="text-green-vivid inline-flex items-center">
                      <Check aria-hidden="true" size={14} strokeWidth={3} />
                      <span className="sr-only">Guardado</span>
                    </span>
                  ) : day.planned ? (
                    <span className="text-yellow-vivid text-[11px] font-bold">Planificado</span>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>

        <IconButton
          label="Día siguiente"
          tooltip="Día siguiente"
          icon={<ChevronRight size={16} />}
          variant="ghost"
          size="sm"
          isDisabled={!hasNext}
          onClick={() => hasNext && onSelect(days[selectedIndex + 1].date)}
        />
      </div>
    </div>
  );
}
